<template>
  <div class="user-activity">
    <div class="user-activity-toolbar">
      <v-col cols="2">
        <OrgSelect v-model="selectedOrg" :disabled="loading" @orgName="handleOrgName"/>
      </v-col>
      <v-col cols="2">
        <div class="date-picker">
          <material-date-time-range 
            :start-value="startValue" 
            :local-time="true"
            @change="range => selectDateRange = range">
          </material-date-time-range>
        </div>
      </v-col>
      <v-col cols="2">
        <v-text-field v-model="search" append-icon="mdi-magnify" label="Search" single-line hide-details />
      </v-col>
      <div class="actions">
        <v-icon :disabled="!allowActions" style="margin-right: 10px" @click="getTableData"> mdi-refresh </v-icon>
        <v-icon :disabled="!allowActions" style="margin-right: 10px" @click="download">mdi-download</v-icon>
      </div>
    </div>  
  
    <v-data-table
      :headers="headers"
      :items="report"
      :search="search"
      item-key="key"
      :loading="loading"
      loading-text="Loading... Please wait"
      :items-per-page="-1"
    >    
    </v-data-table>
  </div>
</template>

<script>

import { mapMutations } from "vuex"
import { Logger } from "aws-amplify"
import {
  ListOrgUsers,
  UserActivityReport
} from "@/services/API.js"
import { 
  parseCSV,
  addTimeOffsets
} from "@/utils";
import OrgSelect from "@/components/general/OrgSelect"
import moment from "moment"
import xlsx from 'xlsx'
import { AmplifyEventBus } from "aws-amplify-vue"
import { userActivity } from '@/mixins/user-activity.js' 

const logger = new Logger("User Activity Report")

export default {
  components: {
    OrgSelect
  },
  mixins: [userActivity],
  data () {
    return {
      startValue: [
        moment().startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        moment().endOf("day").format("YYYY-MM-DDTHH:mm:ss")
      ],
      selectDateRange: [
        moment().startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        moment().endOf("day").format("YYYY-MM-DDTHH:mm:ss")
      ],
      selectedOrg: false,
      orgName: '',
      selectedFilter: [],
      tableData: [],
      loadingTimeout: false, // we use this to clear any loading in the event of an error since some of the loading is non observable
      loading: false,
      search: '', 
      headers: [
        {
          "text": "User",
          "value": "user_name",
          "align": "left"
        },
        {
          "text": "Activity",
          "value": "activity",
          "align": "left"
        },
        {
          "text": "Time (UTC)",
          "value": "activity_time",
          "align": "left"
        },
        {
          "text": "Status",
          "value": "activity_status",
          "align": "left"
        },
        {
          "text": "Additional Information",
          "value": "info",
          "align": "left"
        }
      ],
      users: [],
      report: []
    }
  },
  computed: {
    dateRange () {
      const offset = moment().format("Z") // tz offset, no dst
      const convertToUtc = addTimeOffsets(offset, '+00:00', true) // flipped offset to go from local to UTC

      return this.selectDateRange.map(ts => `${moment.utc(ts).utcOffset(convertToUtc).format('YYYY-MM-DDTHH:mm:ss')}Z`)
    },
    allowActions() {
      return this.report.length > 0 ? true : false
    }
  },
  watch: {
    selectedOrg() {
      this.getTableData();
    },
    selectDateRange() {
      this.getTableData();
    }
  },
  methods: {
    ...mapMutations(["setOrgToWatch"]),
    download () {
      if (this.report.length > 0) {
        // create our excel file
        const wb = xlsx.utils.book_new()
        
        // create our worksheets
        const report_data = this.report.map(i => {
          let entry = {}

          for(let x = 0; x < this.headers.length; x++) {
            entry[this.headers[x].text] = i[this.headers[x].value]
          }
         return entry 
        })

        const report = xlsx.utils.json_to_sheet(report_data)
        const reportName = `${this.orgName}_activity`

        // apend them to the workbook
        xlsx.utils.book_append_sheet(wb, report, reportName)

        xlsx.writeFile(wb, reportName + ".xlsx")
      }
    },
    async getTableData() {
      this.loading = true
      this.report = []

      try {
        const userList = await ListOrgUsers(this.selectedOrg, true)

        if (userList && userList.Users.length > 0) {
          this.users = userList.Users.map((user) => {
            user.name = `${user.Profile.firstname} ${user.Profile.lastname}`

            return user
          })
          
          const emails = userList.Users.map((user) => {
            user.status = !user.disabled // we want the switch to be off if the user is disabled
            return user.email
          })

          const reportURL = await UserActivityReport(this.selectedOrg, this.dateRange, emails);

          if(reportURL) {
            const parsedData = await this.parseCSV(reportURL);

            if(parsedData) {
              this.report = parsedData.map(item => ({
                user_name: this.mapEmailToName(item.entity_id),
                activity: this.userActivityMap(item.action),
                activity_time: item.timestamp,
                activity_status: this.userActivityMap(item.context_status),
                info: item.context_message
              }))
            }
          }

          this.loading = false
        }
        else {
          this.loading = false
          AmplifyEventBus.$emit("snackEvent", {
            color: "error",
            text: 'There are no users in the selected organisation'
          })

        }

      } catch {
        AmplifyEventBus.$emit("snackEvent", {
          color: "error",
          text: 'An error occurred - please try again later'
        })
        this.loading = false
      }
        
      
    },
    handleOrgName(name) {
      this.orgName = name;
    }, 
    mapEmailToName(email) {
      const user = this.users.find(u => u.email === email);
      return user ? user.name : email;
    },
    parseCSV(data) {
      return new Promise(async (res, rej)  => {
       try {
          const trackingFile = await fetch(data)
          const trackingData = await trackingFile.text()
          logger.debug("parseCSV1", JSON.stringify(trackingData))
          const parsedData = parseCSV(trackingData)
          logger.debug('parseCSV2', data, JSON.parse(JSON.stringify(parsedData)));
          res(parsedData)
       } catch (err) {  
        AmplifyEventBus.$emit("snackEvent", {
          color: "error",
          text: 'An error occurred - please try again later'
        });
        rej(err)
       }
      });
    },
  }
}
</script>

<style lang="scss" scoped>
.user-activity {
  padding: 1em;
  .user-activity-toolbar {
    display: flex; 
    align-items: baseline; 
  }
  .date-picker {
    top: 40px;
    position: absolute;
    width: 245px;
  }
  .actions {
    position: absolute;
    right: 20px;
    top: 10px;
  }
}
</style>